<script lang="ts" setup>
import { maska as vMask } from 'maska'
import { useFieldWithErrors } from '@consumer/composables/fieldWithErrors'
import { uniqueId } from '@consumer/helpers/string'
import { Dropdown, type Placement } from 'floating-vue'
import { useValidation } from '@consumer/composables/deprecatedVuelidate'
import type { IconDefinition } from '@fortawesome/fontawesome-common-types'
import type { AllowedComponentProps, StyleValue } from 'vue'
import warningIcon from '@consumer/assets/icons/warning.svg?svgicon'

defineOptions({
  inheritAttrs: false,
})

const {
  type = 'text',
  id,
  modelValue,
  value,
  multiline = false,
  autoresize,
  label,
  hint,
  placeholder,
  placeholderAlignment = 'left',
  icon,
  iconRight,
  hasError = false,
  error,
  errorTooltip = false,
  mask,
  wrapperClass,
  wrapperStyle,
  innerWrapperClass,
  validations,
  size = 'large',
  pill = false,
} = defineProps<{
  type?: string
  id?: string
  modelValue?: string | number | null
  value?: any | null
  multiline?: boolean
  autoresize?: boolean // Only applicable with multiline: true
  label?: string
  hint?: string
  placeholder?: string
  placeholderAlignment?: 'left' | 'center' | 'right'
  icon?: string | IconDefinition
  iconRight?: string | IconDefinition
  hasError?: boolean
  error?: any
  errorTooltip?: boolean | Boolean | Placement | undefined
  mask?: string
  size?: 'small' | 'large'
  inputSize?: number
  wrapperClass?: AllowedComponentProps['class']
  wrapperStyle?: StyleValue
  innerWrapperClass?: AllowedComponentProps['class']
  validations?: any

  // Provided automatically by GInput.
  hasErrors?: boolean
  pill?: boolean
}>()
let focused = $ref(false)

const validator = useValidation(
  { value: validations ?? {} },
  { value: computed(() => modelValue ?? value) } as any,
)

const inputId = computed(() => id ?? uniqueId('g-input-'))

const emit = defineEmits<{
  'update:modelValue': [value: string]
  'update:activated': [value: boolean]
  focus: [value: FocusEvent]
  blur: [value: FocusEvent]
  input: [value: InputEvent]
  keydown: [value: KeyboardEvent]
  keyup: [value: KeyboardEvent]
  paste: [value: ClipboardEvent]
}>()

const inputElement = ref<HTMLInputElement | HTMLTextAreaElement>()

const {
  activate,
  showError,
  errorMessages,
  errorTooltipPlacement,
} = $(useFieldWithErrors(
  inputElement,
  () => error ?? validator.value.$errors,
  () => errorTooltip,
  () => hasError,
))

const slots = useSlots()
const wrapper = useTemplateRef('wrapper')
const hasAddonLeft = computed(() => Boolean(slots.addonLeft || icon))
const hasInnerSlot = $computed(() => Boolean(slots.inner))

const hasAddonRight = computed(() => Boolean(
  slots.addonRight || iconRight || (showError && !multiline),
))

const hasAddonBottom = computed(() => Boolean(slots.addonBottom))

function onInput (event: InputEvent) {
  const inputEl = event.target as HTMLInputElement | HTMLTextAreaElement
  emit('update:modelValue', inputEl?.value)
  emit('input', event)
}

function onKeypress (event: KeyboardEvent) {
  activate()
  if (event.key === 'Enter' && !multiline) {
    (event.target as HTMLInputElement).blur()
  }
}

function onFocus (event: FocusEvent) {
  focused = true
  activate()
  inputElement.value?.focus()
  emit('focus', event)
}

function onBlur (event: FocusEvent) {
  const relatedTarget = event.relatedTarget as HTMLElement | null
  if (wrapper.value?.contains(relatedTarget) || wrapper.value === relatedTarget) {
    // Focus is still within the wrapper, do not blur
    event.stopPropagation()
    event.preventDefault()
    return
  }

  focused = false
  emit('blur', event)
}

defineExpose({
  inputElement,
  $el: inputElement,
  activate,
  focus: () => {
    inputElement.value?.focus()
    focused = true
  },
  blur: () => {
    inputElement.value?.blur()
    focused = false
  },
  select () { inputElement.value?.select() },
})
</script>

<template>
  <fieldset
    ref="wrapper"
    class="group g-input-field relative"
    :class="wrapperClass"
    :style="wrapperStyle"
  >
    <div v-if="label && type != 'hidden'" class="flex items-center gap-1.5 mb-2.5">
      <slot name="label">
        <GLabel :for="inputId">
          <span v-html="label"/>
        </GLabel>
      </slot>

      <span class="font-normal text-sm text-v2-secondary">
        <slot name="hint">
          <span v-html="hint"/>
        </slot>
      </span>
    </div>
    <div class="g-input-group relative" @click="focused = true">

      <!-- Optional left icon, either provide icon name as :icon prop or customize template #icon -->
      <div
        v-if="hasAddonLeft"
        class="
          absolute inset-y-0 flex items-center pointer-events-none
          left-0 pl-4 g-input-icon g-input-icon-left
        "
      >
        <slot name="addonLeft">
          <GIcon v-if="icon" :icon="icon" size="sm" class="text-v2-tertiary"/>
        </slot>
      </div>

      <div
        class="
          g-input
          flex flex-wrap items-center gap-1
          bg-white
        "
        :class="[
          `g-input-${size}`,
          `g-input-type-${type}`,
          innerWrapperClass,
          {
            'g-input-text': type !== 'range',
            'with-inner-slot': hasInnerSlot,
            'with-addon-left': hasAddonLeft,
            'with-addon-right': hasAddonRight,
            focused,
            invalid: hasErrors || showError || hasError,
            'rounded-full': pill,
            'rounded-v2md': !pill,
          },
        ]"
      >

        <slot name="inner"/>

        <!-- Input can be customized through template #input -->
        <slot v-bind="{ type, modelValue, placeholder, mask, hasAddonLeft, hasAddonRight, inputId, focused }">
          <component
            :is="multiline ? 'textarea' : 'input'"
            :id="inputId"
            ref="inputElement"
            v-mask="mask"
            v-bind="$attrs"
            class="g-inner-input flex-grow font-medium focus-visible:outline-none flex-grow"
            :class="[
              {
                'pl-1': hasInnerSlot,
                'px-5 py-4 w-full': !hasInnerSlot,
                'pl-2': hasAddonLeft,
                'rounded-v2md': size === 'large' && !pill,
                'rounded-full': pill,
                multiline,
                autoresize: autoresize ?? multiline,
              },
            ]"
            :type="type === 'date_native' ? 'date' : type"
            :value="modelValue || value"
            :placeholder="placeholder"
            :size="inputSize"
            @input="onInput"
            @keypress="onKeypress"
            @keydown="emit('keydown', $event)"
            @keyup="emit('keyup', $event)"
            @focus="onFocus"
            @blur="onBlur"
            @paste="$emit('paste', $event)"
          />
        </slot>

        <div
          v-if="hasAddonBottom"
          class="
            addon-bottom
            flex items-center
            g-input-icon g-input-icon-bottom
            w-full
            text-v2-secondary
          "
        >
          <slot name="addonBottom"/>
        </div>

      </div>

      <slot name="suffix"/>

      <Dropdown
        v-if="errorTooltip && showError"
        shown
        :triggers="[]"
        :autoHide="false"
        :flip="false"
        :placement="errorTooltipPlacement"
        popperClass="g-input-field-error-tooltip"
        :arrowPadding="8"
        :distance="8"
      >
        <template #popper>
          <ul class="p-4 px-6 text-red-base">
            <li v-for="errorMessage, i in errorMessages" :key="i">
              {{ errorMessage }}
            </li>
          </ul>
        </template>
      </Dropdown>

      <!-- Optional right icon, either provide icon name as :icon prop or customize template #iconRight-->
      <div
        v-if="hasAddonRight"
        class="
          addon-right
          absolute inset-y-0 flex items-center
          right-0 pr-4 g-input-icon g-input-icon-right
          text-v2-secondary
        "
      >
        <slot name="addonRight">
          <GIcon v-if="iconRight" :icon="iconRight"/>
          <GIcon v-else :svg="warningIcon" class="text-red-base"/>
        </slot>
      </div>
    </div>

    <slot v-if="showError && !errorTooltip" name="errorMessage">
      <GInputError :error="errorMessages"/>
    </slot>
  </fieldset>
</template>

<style lang="scss">
.g-input-field .svg-inline--fa {
  margin-top: -0.125em;
}

.g-input {
  --br: var(--grey-primary);

  border: 1px solid var(--br);

  &[type="hidden"] {
    border: 0;
  }

  &:hover,
  &.focused {
    --br: var(--theme-color-primary-base);
  }

  &.focused {
    @apply z-10;

    box-shadow: 0 0 0 1px var(--br);
  }

  &.invalid {
    --br: theme("colors.red.base");

    color: var(--br);
  }

  &.with-inner-slot {
    @apply px-5 py-4;
  }

  &.g-input-small {
    @apply rounded-v2sm py-2 px-3;

    .g-inner-input {
      @apply text-sm p-0;
    }
  }

  &.with-addon-left {
    @apply pl-8;
  }

  &.with-addon-right {
    @apply pr-8;
  }
}

.g-inner-input {
  &::placeholder {
    text-align: v-bind("placeholderAlignment");
  }
}

.g-input-text {
  @apply text-base text-navy placeholder-v2-tertiary;

  .g-inner-input {
    &::placeholder {
      @apply text-v2-tertiary;
    }
  }
}

.g-inner-input.autoresize {
  // stylelint-disable-next-line property-no-unknown
  field-sizing: content;
  height: auto;
  min-height: 78px;
  resize: none;
}

.g-input-field-error-tooltip {
  .v-popper__inner {
    @apply max-w-md sm:mx-6 lg:mx-0;
  }
}

.g-input-type-hidden {
  display: none;
}
</style>
